import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'

import { ASSIGNABLE_ROLES, ROLES, type AssignableRoleId } from './roles'
import { ApiException, api } from '../../api/client'
import type { CreateUserBody, TeamMember } from '../../api/types.gen'
import { Banner, Button, Dialog, Field, cx } from '../../ui'

interface Props {
  onClose: () => void
  onAdded: () => void
}

/**
 * Añadir a alguien al equipo.
 *
 * Ya no se pide contraseña: se crea la cuenta en estado `invited` y le llega un
 * correo con un enlace para que elija la suya. Antes había que inventársela y
 * pasársela por WhatsApp, que es exactamente el hábito que un producto no
 * debería enseñar — y además dejaba esa contraseña en el historial de un chat.
 *
 * Si el correo no sale, se dice aquí mismo y se ofrece reenviarlo desde la
 * lista, en vez de dejar a alguien creado y sin forma de entrar.
 */
export function AddMemberDialog({ onClose, onAdded }: Props) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<AssignableRoleId>('member')
  const [failure, setFailure] = useState<string | null>(null)

  const ready = name.trim() !== '' && email.trim() !== ''

  const create = useMutation({
    mutationFn: () =>
      api.post<{ data: TeamMember; meta: { invitation_sent: boolean } }, CreateUserBody>('/users', {
        name: name.trim(),
        email: email.trim(),
        role,
      }),
    onSuccess: (result) => {
      onAdded()

      /*
       | Si el correo no salió, la persona existe pero no puede entrar. Cerrar
       | el diálogo sin decirlo la dejaría esperando un correo que nunca llegó.
       | Se avisa y se queda abierto para que se lea.
       */
      if (!result.meta.invitation_sent) {
        setFailure(
          'La cuenta se creó, pero no pudimos enviarle el correo. Reenvíaselo desde la lista.',
        )

        return
      }

      onClose()
    },
    onError: (error) => {
      setFailure(
        error instanceof ApiException
          ? error.message
          : 'No se pudo añadir. Revisa tu conexión.',
      )
    },
  })

  return (
    <Dialog
      open
      onClose={onClose}
      title="Añadir a tu equipo"
      description="Le llegará un correo para que elija su contraseña."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {create.isSuccess ? 'Cerrar' : 'Cancelar'}
          </Button>
          <Button
            disabled={!ready || create.isSuccess}
            busy={create.isPending && 'Enviando…'}
            onClick={() => {
              setFailure(null)
              create.mutate()
            }}
          >
            Enviar invitación
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <Field
          label="Nombre"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Beto Ramos"
          autoFocus
        />

        <Field
          label="Email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          inputMode="email"
          autoCapitalize="none"
          autoCorrect="off"
          hint="Le mandamos aquí el enlace para que elija su contraseña."
        />

        <fieldset className="border-0 p-0">
          <legend className="text-label font-semibold text-ink">¿Qué va a poder hacer?</legend>

          <div className="mt-3 flex flex-col gap-2">
            {ASSIGNABLE_ROLES.map((id) => (
              <label key={id} className="block cursor-pointer">
                <input
                  type="radio"
                  name="role"
                  value={id}
                  checked={role === id}
                  onChange={() => setRole(id)}
                  className="peer sr-only"
                />
                <span
                  className={cx(
                    'block rounded-card border p-3 transition-colors',
                    'peer-focus-visible:ring-2 peer-focus-visible:ring-accent',
                    role === id
                      ? 'border-accent bg-accent-subtle'
                      : 'border-line hover:border-line-strong',
                  )}
                >
                  <span className="block text-label font-medium text-ink">{ROLES[id].label}</span>
                  <span className="mt-0.5 block text-body text-ink-muted">{ROLES[id].summary}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        {failure !== null && (
          <Banner tone="danger" title="Revisa esto" live="alert">
            {failure}
          </Banner>
        )}
      </div>
    </Dialog>
  )
}
